import {engineContext} from './context.js';

(function(global){'use strict';
const VERSION='0.1.0-alpha';

function clone(x){ return x==null?x:JSON.parse(JSON.stringify(x)); }

function create(opts){
  opts=opts||{};
  const repository=opts.repository;
  const adapter=opts.adapter||global.ZooInvestResearchStatePersistenceAdapter;
  if(!repository) throw new Error('REPOSITORY_REQUIRED');
  if(!adapter) throw new Error('PERSISTENCE_ADAPTER_REQUIRED');

  async function restore(symbol,scope){
    const loaded=await repository.load(symbol,scope);
    if(!loaded.found){
      return Object.freeze({
        status:'EMPTY',
        symbol,
        scope:scope||'RESEARCH',
        record:null,
        hydration:null,
        reviewRequired:false
      });
    }
    if(loaded.error){
      return Object.freeze({
        status:'REVIEW',
        symbol,
        scope:scope||'RESEARCH',
        record:null,
        hydration:null,
        reviewRequired:true,
        reason:loaded.error,
        issues:loaded.issues||[]
      });
    }
    const r=clone(loaded.record);
    const hydration={
      evidenceFingerprint:r.evidenceFingerprint||null,
      thesisFingerprint:r.thesisFingerprint||null,
      snapshot:clone(r.snapshot||null),
      readiness:clone(r.readiness||null),
      integrity:clone(r.integrity||null),
      brief:clone(r.brief||null),
      completionMemory:clone(r.completionMemory||[]),
      openGaps:clone(r.openGaps||[]),
      invalidations:clone(r.invalidations||[]),
      metadata:clone(r.metadata||{})
    };
    return Object.freeze({
      status:'RESTORED',
      symbol:r.symbol,
      scope:r.scope,
      record:Object.freeze(r),
      hydration:Object.freeze(hydration),
      reviewRequired:false
    });
  }

  function hydrateIntoTargets(restored,targets){
    targets=targets||{};
    if(!restored || restored.status!=='RESTORED'){
      return Object.freeze({hydrated:false,reason:'NOT_RESTORED',applied:Object.freeze([])});
    }
    const h=restored.hydration||{};
    const applied=[];

    function apply(name,value){
      const target=targets[name];
      if(!target || typeof target.hydrate!=='function') return;
      target.hydrate(clone(value));
      applied.push(name);
    }

    apply('snapshot',h.snapshot);
    apply('readiness',h.readiness);
    apply('integrity',h.integrity);
    apply('brief',h.brief);
    apply('completionMemory',h.completionMemory);
    apply('openGaps',h.openGaps);
    apply('invalidations',h.invalidations);
    apply('metadata',h.metadata);

    return Object.freeze({
      hydrated:true,
      applied:Object.freeze(applied),
      evidenceFingerprint:h.evidenceFingerprint||null,
      thesisFingerprint:h.thesisFingerprint||null
    });
  }

  async function bootstrap(symbol,scope,targets){
    const restored=await restore(symbol,scope);
    if(restored.status!=='RESTORED'){
      return Object.freeze({
        status:restored.status,
        restored,
        hydrationResult:null
      });
    }
    const hydrationResult=hydrateIntoTargets(restored,targets);
    return Object.freeze({
      status:hydrationResult.hydrated?'READY':'REVIEW',
      restored,
      hydrationResult
    });
  }

  return Object.freeze({VERSION,restore,hydrateIntoTargets,bootstrap});
}

async function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});

  const repoEmpty={load:async()=>({found:false,record:null})};
  const adapter={};
  const a=create({repository:repoEmpty,adapter});
  const e=await a.restore('AAA','OWNED');
  ok('empty repository returns EMPTY',e.status==='EMPTY');
  ok('empty restore does not require review',e.reviewRequired===false);

  const repoBad={load:async()=>({found:true,error:'INVALID_RECORD',issues:['x']})};
  const b=create({repository:repoBad,adapter});
  const rbad=await b.restore('AAA','OWNED');
  ok('invalid repository record returns REVIEW',rbad.status==='REVIEW'&&rbad.reviewRequired===true);
  ok('invalid record preserves reason',rbad.reason==='INVALID_RECORD');

  const record={
    symbol:'AAA',scope:'OWNED',
    evidenceFingerprint:'e1',thesisFingerprint:'t1',
    snapshot:{a:1},readiness:{state:'REVIEW'},integrity:{state:'HEALTHY'},
    brief:{attention:'NORMAL'},completionMemory:[{id:1}],
    openGaps:[{id:'g'}],invalidations:[{id:'i'}],metadata:{x:1}
  };
  const repoGood={load:async()=>({found:true,record})};
  const c=create({repository:repoGood,adapter});
  const rr=await c.restore('AAA','OWNED');
  ok('valid record restores',rr.status==='RESTORED');
  ok('fingerprints restored',rr.hydration.evidenceFingerprint==='e1'&&rr.hydration.thesisFingerprint==='t1');
  ok('completion memory restored',Array.isArray(rr.hydration.completionMemory)&&rr.hydration.completionMemory.length===1);

  const calls=[];
  const targets={
    snapshot:{hydrate:v=>calls.push(['snapshot',v])},
    readiness:{hydrate:v=>calls.push(['readiness',v])},
    integrity:{hydrate:v=>calls.push(['integrity',v])},
    brief:{hydrate:v=>calls.push(['brief',v])}
  };
  const hr=c.hydrateIntoTargets(rr,targets);
  ok('hydrateIntoTargets succeeds',hr.hydrated===true);
  ok('known targets applied',hr.applied.length===4);
  ok('snapshot hydrator called',calls.some(x=>x[0]==='snapshot'));
  ok('readiness hydrator called',calls.some(x=>x[0]==='readiness'));

  const boot=await c.bootstrap('AAA','OWNED',targets);
  ok('bootstrap reaches READY',boot.status==='READY');
  ok('bootstrap exposes hydration result',boot.hydrationResult&&boot.hydrationResult.hydrated===true);

  const badHydrate=c.hydrateIntoTargets({status:'EMPTY'},{});
  ok('non-restored state cannot hydrate',badHydrate.hydrated===false);

  const api=create({repository:repoGood,adapter});
  ok('orchestrator exposes no trade signal',!('tradeSignal' in api));
  ok('orchestrator exposes no capital permission',!('capitalPermission' in api));

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={VERSION,create,selfTest};
global.ZooInvestResearchStateBootstrapOrchestrator=Object.freeze(API);
})(engineContext);